/**
 * ChangeRoleDialog — assign a member to a granular role (ADR-0030).
 *
 * The picker reads the workspace's roles from `roles.list` (the same list the
 * Settings ▸ Roles & org editor manages) and saves via `roles.assign`, which
 * syncs the legacy `memberships.role` band and enforces last-admin protection
 * server-side (CONFLICT 409 `cause.reason: 'last-admin-protected'`). Before
 * ADR-0030 this dialog hardcoded the three legacy bands and called
 * `team.changeRole`; that left the member side disconnected from the roles a
 * workspace actually defines.
 */
import * as React from "react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";

export interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: { membershipId: string; user: { name: string }; roleId: string | null } | null;
  onChanged: () => void;
}

export function ChangeRoleDialog({ open, onOpenChange, member, onChanged }: ChangeRoleDialogProps) {
  const roles = trpc.roles.list.useQuery({}, { enabled: open });
  const [roleId, setRoleId] = React.useState<string>(member?.roleId ?? "");

  React.useEffect(() => {
    if (open) setRoleId(member?.roleId ?? "");
  }, [open, member]);

  const assign = trpc.roles.assign.useMutation({
    onSuccess: () => {
      onChanged();
      onOpenChange(false);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!member || !roleId) return;
    if (roleId === member.roleId) {
      onOpenChange(false);
      return;
    }
    assign.mutate({ membershipId: member.membershipId, roleId });
  };

  const wireError = assign.error?.message ?? null;
  const roleList = roles.data?.roles ?? [];
  const selected = roleList.find((r) => r.id === roleId) ?? null;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Change role</DialogTitle>
            <DialogDescription>
              {member ? (
                <>
                  Updating <strong>{member.user.name}</strong>&apos;s role.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex flex-col gap-3">
            <Label htmlFor="role-select">Role</Label>
            {roles.isLoading ? (
              <p className="text-sm text-muted">Loading roles…</p>
            ) : roles.error ? (
              <Alert intent="bad" title="Couldn't load roles" description={roles.error.message} />
            ) : roleList.length === 0 ? (
              <Alert
                intent="warn"
                title="No roles defined"
                description="Create roles in Settings ▸ Roles & org before assigning members."
              />
            ) : (
              <>
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger id="role-select">
                    <SelectValue placeholder="Select a role…" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[--radix-select-trigger-width]">
                    {roleList.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected?.description ? (
                  <p className="text-xs text-muted leading-relaxed">{selected.description}</p>
                ) : null}
              </>
            )}
            {wireError ? (
              <Alert intent="bad" title="Couldn't change role" description={wireError} />
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={assign.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={assign.isPending || !roleId}>
              {assign.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
